import { create } from 'zustand'

import { AdminCsatTriggerSource } from '~shared/dto'

const ADMIN_CSAT_LAST_SHOWN_KEY = 'adminCsatLastShown'
const COOLDOWN_MS = 28 * 24 * 60 * 60 * 1000

/**
 * 28-day cross-event cooldown, started by dismiss or submit. Lives in
 * localStorage, so an admin on a second browser may be asked twice within
 * the window - accepted trade-off vs tracking prompts on the backend.
 */
export const isInCsatCooldown = (): boolean => {
  try {
    const lastShown = Number(localStorage.getItem(ADMIN_CSAT_LAST_SHOWN_KEY))
    return Boolean(lastShown) && Date.now() - lastShown < COOLDOWN_MS
  } catch {
    // localStorage unavailable: treat as in cooldown so we never nag
    return true
  }
}

export const markCsatShown = (): void => {
  try {
    localStorage.setItem(ADMIN_CSAT_LAST_SHOWN_KEY, String(Date.now()))
  } catch {
    // ignore - worst case the modal shows again next trigger
  }
}

type AdminCsatStore = {
  pendingTrigger: AdminCsatTriggerSource | null
  recordTrigger: (trigger: AdminCsatTriggerSource) => void
  clearTrigger: () => void
}

export const useAdminCsatStore = create<AdminCsatStore>((set) => ({
  pendingTrigger: null,
  recordTrigger: (trigger) =>
    set((state) => ({ pendingTrigger: state.pendingTrigger ?? trigger })),
  clearTrigger: () => set({ pendingTrigger: null }),
}))

/** Callable from non-component code (e.g. mutation onSuccess handlers). */
export const recordAdminCsatTrigger = (
  trigger: AdminCsatTriggerSource,
): void => {
  // Skip the store update entirely while in cooldown so the modal's
  // subscription never fires for a prompt we won't show.
  if (isInCsatCooldown()) return
  useAdminCsatStore.getState().recordTrigger(trigger)
}
